/**
 * Utility functions for icon management
 * Provides helpers to work with icons from the unified registry
 */

import {
  ionicIconsMap,
  fontAwesomeIconsMap,
  heroiconsMap,
  lottieIconPaths,
  lottieIconConfig,
} from './iconsRegistry';
import { logger } from '@/common/services/config';

/**
 * Icon configuration interface
 */
export interface IconConfig {
  type: 'ionic' | 'fontawesome' | 'heroicons' | 'custom' | 'lottie';
  data: any;
  fallback?: string;
  size?: {
    width?: number | string;
    height?: number | string;
  };
  source?: 'animatedicon.co' | 'custom' | 'ionic';
}

// ============================================================================
// ICON MAP GENERATION
// ============================================================================

/**
 * Generic helper to convert icon maps to IconConfig format
 * @param iconMap - Map of icon names to icon data
 * @param type - Type of icons (ionic, fontawesome, heroicons)
 * @returns Record of icon names to IconConfig objects
 */
const createIconsConfig = (
  iconMap: Record<string, any>,
  type: 'ionic' | 'fontawesome' | 'heroicons'
): Record<string, IconConfig> => {
  const config: Record<string, IconConfig> = {};
  Object.entries(iconMap).forEach(([iconName, iconData]) => {
    config[iconName] = { type, data: iconData };
  });
  return config;
};

/**
 * Helper to convert Lottie icons to IconConfig format
 */
const createLottieIconsConfig = (): Record<string, IconConfig> => {
  const lottieConfig: Record<string, IconConfig> = {};
  Object.keys(lottieIconPaths).forEach((iconName) => {
    const config = lottieIconConfig[iconName] || {};
    lottieConfig[iconName] = {
      type: 'lottie',
      data: null, // Loaded dynamically
      size: config.size,
      source: config.source,
    };
  });
  return lottieConfig;
};

/**
 * Unified icon map combining all icon types
 */
const iconMap: Record<string, IconConfig> = {
  ...createIconsConfig(ionicIconsMap, 'ionic'),
  ...createIconsConfig(fontAwesomeIconsMap, 'fontawesome'),
  ...createIconsConfig(heroiconsMap, 'heroicons'),
  ...createLottieIconsConfig(),
};

// ============================================================================
// LOTTIE UTILITIES
// ============================================================================

/**
 * Import Lottie icon using dynamic import (alternative to fetch)
 * @param path - Path to the Lottie JSON file
 * @returns Promise with the Lottie animation data or null
 */
const importLottieIcon = async (path: string) => {
  try {
    const module = await import(/* @vite-ignore */ path);
    return module.default;
  } catch (error) {
    logger.warn(`Impossible de charger l'icône Lottie: ${path}`, error);
    return null;
  }
};

/**
 * Load Lottie icon data using fetch
 * @param iconName - Name of the Lottie icon
 * @returns Promise with the Lottie animation data or null
 */
export const getLottieIconData = async (iconName: string) => {
  const iconPath = lottieIconPaths[iconName];
  if (iconPath) {
    try {
      const response = await fetch(iconPath);
      if (response.ok) {
        return await response.json();
      } else {
        logger.warn(`Impossible de charger l'icône Lottie: ${iconName} (${response.status})`);
        return null;
      }
    } catch (error) {
      logger.warn(`Erreur lors du chargement de l'icône Lottie: ${iconName}`, error);
      return null;
    }
  }
  return null;
};

/**
 * Check if an icon name corresponds to a Lottie icon
 * @param iconName - Name of the icon
 * @returns True if it's a Lottie icon
 */
export const isLottieIcon = (iconName: string | undefined): boolean => {
  if (!iconName) return false;
  return iconName in lottieIconPaths;
};

// ============================================================================
// ICON GETTERS AND UTILITIES
// ============================================================================

/**
 * Get icon configuration by name
 * @param iconName - Name of the icon
 * @returns IconConfig object or default icon if not found
 */
export const getIconByName = (iconName: string | undefined): IconConfig => {
  const defaultIcon: IconConfig = {
    type: 'ionic',
    data: 'alert-circle-outline',
  };

  if (!iconName) return defaultIcon;
  const iconConfig = iconMap[iconName];
  return iconConfig || defaultIcon;
};

/**
 * Get the type of an icon
 * @param iconName - Name of the icon
 * @returns Icon type: 'ionic' | 'fontawesome' | 'heroicons' | 'custom' | 'lottie'
 */
export const getIconType = (
  iconName?: string
): 'ionic' | 'fontawesome' | 'heroicons' | 'custom' | 'lottie' => {
  if (!iconName) return 'ionic';
  const iconConfig = getIconByName(iconName);
  return iconConfig.type;
};

/**
 * Register a custom icon at runtime
 * @param name - Name for the icon
 * @param config - Icon configuration
 */
export const registerIcon = (name: string, config: IconConfig) => {
  iconMap[name] = config;
};

/**
 * Get list of all available icon names
 * @returns Array of icon names
 */
export const getAvailableIcons = () => Object.keys(iconMap);

/**
 * Get the configured size for an icon
 * @param iconName - Name of the icon
 * @returns Size configuration or undefined
 */
export const getIconConfigSize = (iconName: string | undefined) => {
  if (!iconName) return undefined;
  const iconConfig = getIconByName(iconName);
  return iconConfig.size;
};
